import * as React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { canJustify, occurrenceTitle } from "@/features/attention/occurrence-meta";
import { OccurrenceStatusPills } from "@/features/attention/OccurrenceStatusPills";
import type { AttentionOccurrence } from "@/types/attention.types";
import { formatDateTime, getEventTypeLabel } from "@/lib/display-labels";

// Modal de detalhes da ocorrência: todas as infos
// atreladas (máquina, linha, tipo(s)/status, data/hora absoluta, motivo, ação, refs).
// Padrão de overlay alinhado a JustifyEventDialog (fixed, role=dialog, fecha no backdrop/Esc).
export function AttentionDetailDialog({
  occ,
  open,
  onClose,
  onJustify,
}: {
  occ: AttentionOccurrence | null;
  open: boolean;
  onClose: () => void;
  onJustify: (eventId: string) => void;
}): React.ReactElement | null {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !occ) return null;

  const justifiable = canJustify(occ);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- backdrop overlay; Escape fecha via window listener
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-background/80 p-4 motion-safe:animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label="Detalhes da ocorrência"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Card className="w-full max-w-md motion-safe:animate-scale-in">
        <CardContent className="space-y-3 pt-6">
          <div className="flex items-start justify-between gap-2">
            <h2 className="text-base font-semibold text-text-primary">{occurrenceTitle(occ)}</h2>
            <span className="flex flex-wrap items-center justify-end gap-1">
              <OccurrenceStatusPills occ={occ} />
            </span>
          </div>

          <dl className="space-y-2 text-[13px]">
            <Field label="Máquina" value={occ.machineCode ?? "—"} mono />
            <Field label="Linha" value={occ.lineName} />
            {occ.justification ? (
              <Field label="Tipo" value={getEventTypeLabel(occ.justification.type)} />
            ) : null}
            <Field label="Data/hora" value={formatDateTime(occ.since)} mono />
            <Field label="Motivo" value={occ.reason ?? "—"} />
            <Field label="Ação esperada" value={occ.action} />
            {occ.alarm ? <Field label="Ref. alarme" value={occ.alarm.refId} mono /> : null}
            {occ.justification ? (
              <Field label="Ref. evento" value={occ.justification.refId} mono />
            ) : null}
          </dl>

          <div className="flex justify-end gap-2">
            {justifiable ? (
              <Button
                variant="outline"
                onClick={() => {
                  onJustify(occ.justification!.refId);
                  onClose();
                }}
              >
                Justificar
              </Button>
            ) : null}
            <Button onClick={onClose}>Fechar</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): React.ReactElement {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-text-tertiary">{label}</dt>
      <dd
        className={mono ? "text-right font-mono text-text-primary" : "text-right text-text-primary"}
      >
        {value}
      </dd>
    </div>
  );
}
